import { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import { OfferLanguageItem } from '@recruitee/http/types';
import {
  createEntityCollectionStore,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@recruitee/ngrx';

import { EntityStoreKey } from '../../types';
import { OfferLanguagesSetupEntity } from './offer-languages-store.types';

const {
  namespace,
  selectors: defaultSelectors,
  actions: defaultActions,
  reducer,
} = createEntityCollectionStore<OfferLanguagesSetupEntity>()(EntityStoreKey.OfferLanguagesSetups, {
  idSelector: e => e.offerId,
  customActions: adapter => ({
    fetchOne: (state, payload: { offerId: number }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.offerId] }),
    fetchOneSuccess: (state, payload: { scope: null; data: OfferLanguagesSetupEntity }) =>
      pipeState(adapter.upsertOne(state, payload.data), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [payload.data.offerId] }),
      ),
    toggle: (state, payload: { offerId: number; code: string; enabled: boolean }) =>
      pipeState(adapter.markAsPending(state, { scope: null, entities: [payload.offerId] })),
    toggleSuccess: (state, payload: { data: OfferLanguageItem; offerId: number }) => {
      let updatedEntity = state.entityMap[payload.offerId];
      if (!updatedEntity) {
        throw new Error(
          `Trying to update non existing OfferLanguagesSetupEntity w offerId ${payload.offerId}`,
        );
      }
      updatedEntity = { ...updatedEntity };
      const updatedSetup = { ...updatedEntity.setup };
      updatedSetup.languages = updatedSetup.languages.map(language => {
        if (language.code === payload.data.code) return payload.data;
        return language;
      });
      updatedEntity.setup = updatedSetup;

      return pipeState(adapter.updateOne(state, updatedEntity), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [updatedEntity!.offerId] }),
      );
    },
    toggleFailed: (state, payload: { offerId: number; error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.offerId] }),
    setAsPrimary: (state, payload: { offerId: number; code: string }) =>
      pipeState(adapter.markAsPending(state, { scope: null, entities: [payload.offerId] })),
    setAsPrimarySuccess: ngrxEvent<{ offerId: number; code: string }>(),
    setAsPrimaryFailed: (state, payload: { offerId: number; error: HttpErrorResponse }) =>
      pipeState(
        adapter.setError(state, payload.error, { scope: null, entities: [payload.offerId] }),
      ),
  }),
});

const selectors = {
  byOfferId: memoizeSelector((id: number) =>
    createSelector(
      defaultSelectors.byId(id),
      (state: OfferLanguagesSetupEntity | null) => state?.setup || null,
    ),
  ),
};

const actions = {
  fetchOne: defaultActions.fetchOne,
  fetchOneSuccess: defaultActions.fetchOneSuccess,
  fetchOneFailed: defaultActions.fetchOneFailed,
  toggle: defaultActions.toggle,
  toggleSuccess: defaultActions.toggleSuccess,
  toggleFailed: defaultActions.toggleFailed,
  setAsPrimary: defaultActions.setAsPrimary,
  setAsPrimarySuccess: defaultActions.setAsPrimarySuccess,
  setAsPrimaryFailed: defaultActions.setAsPrimaryFailed,
};

export {
  actions as OfferLanguagesActions,
  namespace as OfferLanguagesNamespace,
  selectors as OfferLanguagesSelectors,
  reducer as OfferLanguagesReducer,
};
